import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { updatePatient } from '@/graphql/mutations';
import { getPatient } from '@/graphql/queries';
import { CookieStorage } from 'aws-amplify/utils';
import { eAgreeState } from '@/features/agreement/type/contract';
import { GetPatientQuery, UpdatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';
import { CUSTOM_ERROR_HANDLE, notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeGet, executePost } from '@/client/rest';

// 指定された長さのランダムな文字列を生成するユーティリティ関数
function generateRandomString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

// state 値を生成する関数
const generateState = async (length: number = 32) => {
  // 32文字で約256ビットのエントロピーを提供します
  if (length < 1 || length > 255) {
    throw new Error('長さは1文字以上255文字以下で指定してください。');
  }
  return generateRandomString(length);
};

// nonce 値を生成する関数
const generateNonce = async (length: number = 32) => {
  // 32文字で約256ビットのエントロピーを提供します
  if (length < 1 || length > 255) {
    throw new Error('長さは1文字以上255文字以下で指定してください。');
  }
  return generateRandomString(length);
};

// codeVerifier 値を生成する関数
const generateCodeVerifier = async (length: number = 44) => {
  // 32文字で約256ビットのエントロピーを提供します
  if (length < 1 || length > 128) {
    throw new Error('長さは1文字以上128文字以下で指定してください。');
  }
  return generateRandomString(length);
};

const encryptSha256 = async (str: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return hashBuffer;
};

const toBase64URL = async (str: ArrayBuffer) => {
  const byteArray = new Uint8Array(str);
  let binaryString = '';
  for (let i = 0; i < byteArray.byteLength; i++) {
    binaryString += String.fromCharCode(byteArray[i]);
  }

  // Base64エンコード
  const base64 = btoa(binaryString);
  // Base64URL形式に変換
  return base64
    .replace(/\+/g, '-') // + → -
    .replace(/\//g, '_') // / → _
    .replace(/=+$/, ''); // パディング文字 = を削除
};

// 対象施設に対してマイナンバー認証（本人確認）ができているかどうかを取得
export const getIsMynumberAuth = async () => {
  try {
    const cognitoId: string = (await fetchAuthSession()).userSub || '';
    const facilityId: string = await getFacilityId();
    if (cognitoId === '') throw new Error('IDの取得に失敗しました。');
    // isMynumberAuthを取得
    const data = await executeGet({
      apiName: 'medirecoWebApi',
      path: '/get-is-mynumber-auth',
      options: {
        queryParams: {
          id: cognitoId,
          facilityId: facilityId
        }
      }
    });
    return data;
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }

    console.error(`認証状態の取得に失敗しました。認証フローへ移行します。\nError:${error}`);
    return false;
  }
};

const getFacilityId = async () => {
  try {
    const cookie = new CookieStorage();
    let facilityId: string | '' = '';
    const sessionData = sessionStorage.getItem('facility_id');
    const cookieData = await cookie.getItem('facility_id');
    if (sessionData) {
      facilityId = sessionData;
    } else {
      if (cookieData) {
        facilityId = cookieData;
      } else {
        facilityId = '';
      }
    }
    return facilityId;
  } catch (error) {
    console.log(`施設IDの取得に失敗しました。${error}`);
    return '';
  }
};

export const approval = async () => {
  const domain = window.location.origin;
  const newState = await generateState(32);
  const newNonce = await generateNonce(32);
  const code_verifier = generateCodeVerifier(128);
  const newValue = await encryptSha256(await code_verifier);
  const response_type = 'response_type=code';
  const scope = 'scope=openid name address birthdate gender';
  const client_id_base = import.meta.env.VITE_MYNUMBER_CLIENT_ID;
  const client_id = `client_id=${client_id_base}`;
  const redirect_uri_base = domain + '/auth/success';
  const redirect_uri = `redirect_uri=${redirect_uri_base}`;
  const state = `state=${newState}`;
  const nonce = `nonce=${newNonce}`;
  const code_challenge = `code_challenge=${await toBase64URL(newValue)}`;
  const code_challenge_method = 'code_challenge_method=S256';
  const acr_values = 'acr_values=aal3 crl';
  const base_url = import.meta.env.VITE_MYNUMBER_ENDPOINT;

  const url = `${base_url}?${response_type}&${scope}&${client_id}&${redirect_uri}&${state}&${nonce}&${code_challenge}&${code_challenge_method}&${acr_values}`;

  sessionStorage.setItem('code_verifier', await code_verifier);
  const cookie = new CookieStorage();
  cookie.setItem('code_verifier', await code_verifier);

  try {
    await axios
      .get(url, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Access-Control-Allow-Origin': '*' }
      })
      .catch((error) => console.error(error));
    window.location.href = url;
  } catch (error) {
    console.error('Fetch error:', error);
  }
};

const splitName = (fullName: string): { familyName: string; firstName: string } => {
  const names = fullName.split('　');
  return {
    familyName: names[0] || '',
    firstName: names[1] || ''
  };
};

const formatBirthday = (birthday: number): string => {
  const birthdayStr = birthday.toString();
  return `${birthdayStr.slice(0, 4)}-${birthdayStr.slice(4, 6)}-${birthdayStr.slice(6)}`;
};

export const gettoken = async () => {
  try {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code')?.toString() || '';
    const cookie = new CookieStorage();
    // sessionStorage に値がない場合はcookieから取得
    const code_verifier =
      sessionStorage.getItem('code_verifier') !== null
        ? sessionStorage.getItem('code_verifier')?.toString()
        : (await cookie.getItem('code_verifier'))
          ? await cookie.getItem('code_verifier')
          : '';

    if (!code_verifier) {
      console.error('セッションストレージエラー');
      return;
    }

    type Contract = {
      access_token: string;
      expires_in: string;
      refresh_expires_in: string;
      refresh_token: string;
      token_type: string;
      id_token: string;
      not_before_policy: string;
      session_state: string;
      scope: string;
    };

    const data = await executePost<Contract>({
      apiName: 'medirecoWebApi',
      path: '/post-mynumber-token',
      options: {
        body: {
          code: code,
          code_verifier: code_verifier
        }
      }
    });

    sessionStorage.setItem('access_token', data.access_token);
    cookie.setItem('access_token', data.access_token);
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }

    throw new Error('トークンが無効です');
  }
};

type UserInfo = {
  sub: string;
  name: string;
  address: string;
  birthdate: number;
  gender: number;
};
let info: UserInfo | null = null;

export const getUserInfo = async () => {
  try {
    const cookie = new CookieStorage();
    const session = await fetchAuthSession();
    const cognitoid = session.userSub as string;
    const FacilityId =
      sessionStorage.getItem('facility_id') !== null
        ? sessionStorage.getItem('facility_id')
        : (await cookie.getItem('facility_id')) !== null
          ? await cookie.getItem('facility_id')
          : '';
    if (!FacilityId) {
      return;
    }
    const accessToken =
      sessionStorage.getItem('access_token') !== null
        ? sessionStorage.getItem('access_token')
        : (await cookie.getItem('access_token')) !== null
          ? await cookie.getItem('access_token')
          : '';
    if (!accessToken) {
      return;
    }
    type AddressResponse = {
      state?: string;
      city?: string;
      message?: string;
    };

    const info = (await executeGet({
      apiName: 'medirecoWebApi',
      path: '/get-mynumber-user-info',
      options: {
        queryParams: {
          Authorization: accessToken,
          cognitoId: cognitoid,
          FacilityId: FacilityId
        }
      }
    })) as any;

    const { familyName, firstName } = splitName(info!.name);
    const dateOfBirth = formatBirthday(info!.birthdate);

    // 住所から都道府県と市区町村を取得
    try {
      const addressData = await executePost<AddressResponse>({
        apiName: 'medirecoWebApi',
        path: '/parse-address',
        options: {
          body: {
            address: info!.address
          }
        }
      });

      const session = await fetchAuthSession();
      if (session.userSub) {
        await executeQuery<UpdatePatientMutation>(
          updatePatient,
          {
            input: {
              id: session.userSub,
              familyName: familyName,
              firstName: firstName,
              address: info!.address,
              dateOfBirth: dateOfBirth,
              gender: info!.gender,
              mynumbercard: true,
              state: addressData?.state || null,
              city: addressData?.city || null
            }
          },
          'userPool'
        );
      }
    } catch (addressError) {
      if (addressError instanceof Error && addressError.name !== CUSTOM_ERROR_HANDLE) {
        Sentry.captureException(addressError);
      }

      console.error('住所解析エラー:', addressError);
      // 住所解析に失敗した場合でも、他の情報は更新
      const session = await fetchAuthSession();
      if (session.userSub) {
        const res = await executeQuery<UpdatePatientMutation>(updatePatient, {
          input: {
            id: session.userSub,
            familyName: familyName,
            firstName: firstName,
            address: info!.address,
            dateOfBirth: dateOfBirth,
            gender: info!.gender,
            mynumbercard: true,
            state: null,
            city: null
          }
        });
      }
    }
  } catch (e) {
    if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
      Sentry.captureException(e);
    }

    console.error(e);
    throw new Error('ユーザー情報が無効です');
  }
};

export const getInterview = async () => {
  // 同意判定取得
  const session = await fetchAuthSession();
  const cognitoid = session.userSub as string;
  const cookie = new CookieStorage();

  type Interview = {
    PatientId: string;
    OutpatientId: string;
    ReserveDate: string;
    ReserveTime: string;
  };
  try {
    const result = await executeQuery<GetPatientQuery>(getPatient, { id: cognitoid }, 'userPool');

    const id = result?.getPatient?.id;
    const updateReserveId =
      sessionStorage.getItem('updateReserveId') !== null
        ? sessionStorage.getItem('updateReserveId')
        : (await cookie.getItem('updateReserveId')) !== null
          ? await cookie.getItem('updateReserveId')
          : '';
    const interview = await executeGet<Interview>({
      apiName: 'medirecoWebApi',
      path: '/get-out-patient',
      options: {
        queryParams: {
          id: id || '',
          updateReserveId: updateReserveId || ''
        }
      }
    });

    const FacilityId =
      sessionStorage.getItem('facility_id') !== null
        ? sessionStorage.getItem('facility_id')
        : (await cookie.getItem('facility_id')) !== null
          ? await cookie.getItem('facility_id')
          : '';
    const PatientId = interview.PatientId;
    const OutpatientId = interview.OutpatientId;
    const ReserveDate = interview.ReserveDate;
    const ReserveTime = interview.ReserveTime;
    const Domain = import.meta.env.VITE_INTERVIEW_URL;

    const url = `${Domain}/${FacilityId}/${PatientId}/${OutpatientId}/${ReserveDate}/${ReserveTime}`;
    window.location.href = url;
  } catch (e) {
    if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
      Sentry.captureException(e);
    }

    // TODO: fetchAuthSession
    console.log(e);
  }
};

export const getWallet = async () => {
  type AgreeStatus = {
    InfoTradeAgreeId: string;
    InfoTradeAgreeVer: string;
    TermAgreeId: string;
    TermAgreeVer: string;
  };
  try {
    // すでにウォレットを作成済みかどうかを判定する
    const session = await fetchAuthSession();
    const res = await executeQuery<GetPatientQuery>(getPatient, { id: session.userSub as string }, 'userPool');

    if (res?.getPatient?.isWalletCreate) {
      // 作成済みなら処理中断
      console.log('ウォレット作成済み');
      // 事前問診画面へ
      await getInterview();
      return;
    }
  } catch (e) {
    // TODO: fetchAuthSession
    console.error('An error occurred while getting isWalletCreate.' + e);
    throw new Error('An error occurred while getting isWalletCreate.');
  }

  const cookie = new CookieStorage();
  const FacilityId =
    sessionStorage.getItem('facility_id') !== null
      ? sessionStorage.getItem('facility_id')
      : (await cookie.getItem('facility_id')) !== null
        ? await cookie.getItem('facility_id')
        : '';
  if (!FacilityId) {
    return;
  }

  // 同意状態の確認
  try {
    const agreeStatus: {
      TermAgreeStatus: number;
      InfoTradeAgreeStatus: number;
    } = await executeGet({
      apiName: 'medirecoWebApi',
      path: '/get-agree-status',
      options: {
        queryParams: {
          id: FacilityId,
          customerId: (await fetchAuthSession()).userSub!
        } // 施設ID仮置き
      }
    });

    if (agreeStatus.InfoTradeAgreeStatus === eAgreeState.notAgree) {
      // 取引同意不同意ならウォレット作成せず事前問診へ
      console.log('取引同意不同意');
      // 事前問診画面へ
      await getInterview();
      return;
    }
  } catch (e) {
    if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
      Sentry.captureException(e);
    }

    console.error('An error occurred while getting agreeStatus.' + e);
    throw new Error('An error occurred while getting agreeStatus.');
  }

  const agreeStatus = await executeGet<AgreeStatus>({
    apiName: 'medirecoWebApi',
    path: '/get-agree-info',
    options: {
      queryParams: {
        id: FacilityId,
        customerId: (await fetchAuthSession()).userSub!
      } // 施設ID仮置き
    }
  });

  const InfoTradeAgreeId = agreeStatus.InfoTradeAgreeId;
  const InfoTradeAgreeVer = agreeStatus.InfoTradeAgreeVer;
  const TermAgreeId = agreeStatus.TermAgreeId;
  const TermAgreeVer = agreeStatus.TermAgreeVer;

  // 同意判定取得
  const session = await fetchAuthSession({ forceRefresh: true });
  const cognitoid = session.userSub as string;
  const idtoken = session.tokens?.idToken?.toString();
  try {
    const result = await executeQuery<GetPatientQuery>(getPatient, { id: cognitoid }, 'userPool');

    const id = result?.getPatient?.id;

    await executePost({
      apiName: 'medirecoWebApi',
      path: '/get-wallet-parts',
      options: {
        body: {
          idToken: idtoken!,
          cognitoId: cognitoid,
          InfoTradeAgreeId: InfoTradeAgreeId,
          InfoTradeAgreeVer: InfoTradeAgreeVer,
          TermAgreeId: TermAgreeId,
          TermAgreeVer: TermAgreeVer,
          info: info
        }
      }
    });

    await executeQuery<UpdatePatientMutation>(updatePatient, {
      input: {
        id: session.userSub!,
        isWalletCreate: true
      }
    });
  } catch (e) {
    if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
      Sentry.captureException(e);
    }

    console.error('An error occurred in the function getWallet.' + e);
    throw new Error('An error occurred in the function getWallet.');
  }
};

export const getMynumberState = async () => {
  const session = await fetchAuthSession();
  const cognitoid = session.userSub as string;
  const result = await executeQuery<GetPatientQuery>(getPatient, { id: cognitoid }, 'userPool');
  const mynumbercard = result.getPatient?.mynumbercard;

  return mynumbercard;
};

export const getWalletState = async () => {
  const session = await fetchAuthSession();
  const cognitoid = session.userSub as string;
  const result = await executeQuery<GetPatientQuery>(getPatient, { id: cognitoid }, 'userPool');
  const isWalletCreate = result.getPatient?.isWalletCreate;

  return isWalletCreate;
};
